import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy,
  Car,
  PhoneCall,
  MapPin,
  ClipboardList,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import heroBg from "../assets/hearse.jpg"; 
import vehicle1 from "../assets/vehicles/hearse1.jpg";
import vehicle2 from "../assets/vehicles/hearse2.jpg";
import vehicle3 from "../assets/vehicles/hearse3.jpg";
// ← your image


/* ================= PAGE ================= */
export default function Home() {
  const navigate = useNavigate();
  const [dark, setDark] = useState(true);

  /* ================= THEME SYNC ================= */
  useEffect(() => {
    // initial load
    const loadTheme = () => {
      const t = localStorage.getItem("theme");
      setDark(t !== "light");
    };

    loadTheme();

    // listen to navbar toggle
    const onThemeChange = () => loadTheme();
    window.addEventListener("theme-change", onThemeChange);

    return () => {
      window.removeEventListener("theme-change", onThemeChange);
    };
  }, []);

  const theme = {
    page: dark
      ? "bg-[#0a0a0a] text-white"
      : "bg-gray-50 text-gray-900",

    sectionAlt: dark
      ? "bg-black/40"
      : "bg-gradient-to-b from-gray-100 to-gray-200",

    card: dark
      ? "bg-white/5 border-white/10"
      : "bg-white border-gray-200",

    hover: dark ? "hover:bg-white/10" : "hover:bg-gray-50",

    statBg: dark
      ? "bg-gradient-to-r from-gray-900 to-black"
      : "bg-gradient-to-r from-sky-50 to-white",

    primaryBtn: dark
      ? "bg-gradient-to-r from-gray-700 to-gray-900"
      : "bg-sky-600 hover:bg-sky-700 text-white",

    secondaryBtn: dark
      ? "border-white/20 hover:bg-white/10"
      : "border-gray-300 hover:bg-gray-100",
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 ${theme.page}`}>
      {/* ================= HERO ================= */}
      {/* ================= HERO ================= */}
<motion.section
  initial={{ opacity: 0, y: 60 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.9 }}
  className="relative overflow-hidden"
>
  {/* BACKGROUND IMAGE */}
  <div
    className="absolute inset-0 bg-center bg-cover"
    style={{ backgroundImage: `url(${heroBg})` }}
  />

  {/* OVERLAY (VERY IMPORTANT FOR READABILITY) */}
  <div className="absolute inset-0 bg-black/60" />

  {/* CONTENT */}
  <div className="relative max-w-6xl mx-auto px-6 py-28 text-center text-white">
    <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
      Dignified & Reliable <br /> Hearse Transportation
    </h1>

    <p className="text-lg opacity-90 max-w-3xl mx-auto">
      We provide professional hearse services with dignity, care and
      precision — from mortuary to the final resting place.
    </p>

    <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
  <motion.button
    whileHover={{ scale: 1.05 }}
    onClick={() => navigate("/book")}
    className="px-10 py-4 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold shadow-xl"
  >
    Book Hearse Service
  </motion.button>

  <motion.button
    whileHover={{ scale: 1.05 }}
    onClick={() => navigate("/contact")}
    className="px-10 py-4 rounded-full border border-white/40 hover:bg-white/10 text-white font-semibold"
  >
    Contact Us
  </motion.button>
    </div>
  </div>
</motion.section>


      {/* ================= SERVICE FLOW ================= */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <SectionTitle title="How Our Service Works" />

        <div className="grid md:grid-cols-4 gap-8">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className={`p-6 rounded-2xl border backdrop-blur transition ${theme.card} ${theme.hover}`}
            >
              <s.icon className="mb-4 text-sky-500" size={28} />
              <h4 className="font-semibold mb-2">{s.title}</h4>
              <p className="text-sm opacity-80">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= ROUTES ================= */}
      <section className={`px-6 py-20 ${theme.sectionAlt}`}>
        <SectionTitle title="Hearse Service Routes" />

        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
          {ROUTES.map((r) => (
            <motion.div
              key={r}
              whileHover={{ y: -6 }}
              className={`p-6 rounded-2xl border transition ${theme.card}`}
            >
              <Car className="mb-3 text-emerald-500" />
              <p className="font-semibold">{r}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= OUR VEHICLES ================= */}
<section className="max-w-6xl mx-auto px-6 py-20">
  <SectionTitle title="Our Vehicles" />

  <div className="grid md:grid-cols-3 gap-6">
    {[vehicle1, vehicle2, vehicle3].map((img, i) => (
      <motion.div
        key={i}
        whileHover={{ scale: 1.03 }}
        className={`relative h-64 rounded-2xl overflow-hidden border shadow-sm transition ${theme.card}`}
      >
        <img
          src={img}
          alt={`Hearse Vehicle ${i + 1}`}
          className="w-full h-full object-cover"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition flex items-end p-4">
          <p className="text-white font-semibold">
            Professional Hearse Vehicle
          </p>
        </div>
      </motion.div>
    ))}
  </div>
</section>


      {/* ================= TRUST ================= */}
      <section className={`px-6 py-20 ${theme.statBg}`}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-10 text-center">
          <Stat icon={Users} value="500+" label="Families Served" dark={dark} />
          <Stat icon={ShieldCheck} value="100%" label="Professional Care" dark={dark} />
          <Stat icon={Trophy} value="10+ Years" label="Experience" dark={dark} />
        </div>
      </section>

      {/* ================= CTA ================= */}
      <footer className="px-6 py-24 text-center">
        <motion.h3
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-3xl font-bold mb-6"
        >
          Need Our Service?
        </motion.h3>

        <motion.button
          whileHover={{ scale: 1.05 }}
          onClick={() => navigate("/book")}
          className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-sky-500 text-white font-semibold shadow-xl"
        >
          <PhoneCall /> Request Hearse Service
        </motion.button>
      </footer>
    </div>
  );
}

/* ================= UI ================= */
function SectionTitle({ title }) {
  return <h2 className="text-3xl font-bold text-center mb-14">{title}</h2>;
}

function Stat({ icon: Icon, value, label, dark }) {
  return (
    <div>
      <Icon
        className={`mx-auto mb-4 ${dark ? "text-yellow-400" : "text-sky-600"}`}
        size={36}
      />
      <h4 className="text-3xl font-bold">{value}</h4>
      <p className="opacity-80">{label}</p>
    </div>
  );
}

/* ================= DATA ================= */
const STEPS = [
  {
    icon: ClipboardList,
    title: "Submit Request",
    desc: "Fill the online hearse booking form.",
  },
  {
    icon: PhoneCall,
    title: "Confirmation Call",
    desc: "Our team contacts you immediately.",
  },
  {
    icon: MapPin,
    title: "Route & Pricing",
    desc: "Service planned based on location.",
  },
  {
    icon: Car,
    title: "Service Delivery",
    desc: "Professional hearse transportation.",
  },
];

const ROUTES = [
  "Mortuary → Funeral Ground → Church → Cemetery",
  "Mortuary → Funeral Ground → Cemetery",
  "Mortuary → Cemetery",
];
